<template>
  <div class="nav-wrapper" :style="navWrapperElStyles">
    <header ref="header" :style="headerElStyles" class="navigation">
      <div
        class="outer-wrapper"
        :class="{
          '-menu-open': showMenu,
          '-large': effectiveNavigationBarSize === 'large',
        }"
      >
        <div class="inner-wrapper" :class="{ '-menu-open': showMenu }">
          <div class="top-container">
            <div class="top-row">
              <div class="links-wrapper">
                <a
                  class="logo"
                  :href="jobShopUrl ? jobShopUrl : null"
                  target="_self"
                  title="Homepage"
                  @click="$emit('emittedClick', { type: 'logo' })"
                >
                  <img
                    v-if="
                      brandingGlobal.config.general.logo.media.mediaType ===
                      'image'
                    "
                    class="logo"
                    :src="
                      appendCdnParams(
                        brandingGlobal.config.general.logo.media.url,
                      )
                    "
                    alt="Brand Logo"
                    data-testid="navigation-logo"
                  />
                  <IconCompanyLogo
                    v-else
                    fill="#D9D9D9"
                    width="25"
                    height="25"
                  />
                </a>

                <nav class="desktop-nav" aria-label="Navigation">
                  <ul>
                    <li
                      v-for="(item, index) in navigationGlobal.config.general
                        .links"
                      :key="item.label + '-' + index"
                      :class="{ '-has-dropdown': item.links }"
                    >
                      <!-- Single link -->
                      <a
                        v-if="!item.links"
                        :href="item.url"
                        data-testid="navigation-link-desktop"
                        @click="
                          $emit('emittedClick', {
                            type: 'link',
                            el: item,
                          })
                        "
                      >
                        {{ item.label }}
                      </a>
                      <!-- Group with dropdown -->
                      <template v-else>
                        <NavigationUIFlyout
                          :link-group="item"
                          :styles="flyoutElStyles"
                        />
                      </template>
                    </li>
                  </ul>
                </nav>
              </div>

              <div class="buttons">
                <div class="language-select-desktop">
                  <NavigationUILanguageFlyout
                    v-if="navigationGlobal.config.general.languages.length > 0"
                    :languages="navigationGlobal.config.general.languages"
                    :page-locale="pageLocale"
                    :styles="flyoutElStyles"
                    @emitted-click="(e) => $emit('emittedClick', e)"
                  />
                </div>

                <NavigationCta
                  :cta-button="navigationGlobal.config.ctaButton"
                  :cta-button-el-styles="ctaButtonElStyles"
                  @emitted-click="$emit('emittedClick', $event)"
                />

                <button
                  class="menu-button"
                  :class="{ '-menu-open': showMenu }"
                  aria-label="Toggle menu"
                  @click="toggleMenu"
                >
                  <svg id="menu-icon" viewBox="0 0 100 100">
                    <g :style="menuButtonIconElStyles">
                      <path
                        class="top"
                        :class="{ '-menu-open': showMenu }"
                        d="M 8 25 92 25"
                      ></path>
                      <path
                        class="bottom"
                        :class="{ '-menu-open': showMenu }"
                        d="M 8 75 92 75"
                      ></path>
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <nav v-if="showMenu" class="mobile-nav">
            <transition-group name="slide-fade" tag="ul" appear>
              <li
                v-for="(item, index) in navigationGlobal.config.general.links"
                :key="item.label + '-' + index"
                :style="{ 'transition-delay': (index + 1) * 100 + 'ms' }"
                :class="{
                  '-has-dropdown': item.links,
                  '-active': activeMobileGroup === index,
                }"
              >
                <!-- Single link -->
                <a
                  v-if="!item.links"
                  :href="item.url"
                  data-testid="navigation-link-mobile"
                  @click="$emit('emittedClick', { type: 'link', el: item })"
                >
                  {{ item.label }}
                </a>
                <!-- Group with expandable section -->
                <template v-else>
                  <NavigationUISlidedown
                    :item="item"
                    :index="index"
                    :active-mobile-group="activeMobileGroup"
                    @emitted-click="$emit('emittedClick', $event)"
                    @toggle-group="activeMobileGroup = $event"
                  />
                </template>
              </li>
            </transition-group>
          </nav>
        </div>

        <Transition name="fade">
          <div v-if="showMenu" class="bottom-links">
            <div class="language-select-mobile">
              <NavigationUILanguageSlidedown
                v-if="navigationGlobal.config.general.languages.length > 0"
                :languages="navigationGlobal.config.general.languages"
                :page-locale="pageLocale"
                :index="-2"
                :active-mobile-group="activeMobileGroup"
                @emitted-click="$emit('emittedClick', $event)"
                @toggle-group="activeMobileGroup = $event"
              />
            </div>

            <NavigationCta
              :cta-button="navigationGlobal.config.ctaButton"
              :cta-button-el-styles="ctaButtonElStyles"
              @emitted-click="$emit('emittedClick', $event)"
            />
            <div
              class="social-links"
              :style="{
                color: navigationGlobal.config.general.textColor.colorHex,
              }"
            >
              <template
                v-for="socialChannel in socialMediaGlobal.config.general
                  .socialChannels"
              >
                <a
                  v-if="socialChannel.url"
                  :key="socialChannel.label"
                  target="_blank"
                  :href="socialChannel.url"
                  :title="socialChannel.label"
                >
                  <component
                    :is="getComponentName(socialChannel)"
                    width="25"
                    height="25"
                  />
                </a>
              </template>
            </div>
          </div>
        </Transition>
      </div>
    </header>
  </div>
</template>

<script>
import navigationGlobal from "./navigationDefaultGlobalProps.js";
import socialMediaGlobal from "../../globalSettingProps/socialMediaDefaultGlobalProp.js";
import brandingGlobal from "../../globalSettingProps/brandingDefaultGlobalProp";

import IconCompanyLogo from "../Icons/IconCompanyLogo.vue";
import IconFacebook from "../Icons/Social/IconFacebook.vue";
import IconX from "../Icons/Social/IconX.vue";
import IconInstagram from "../Icons/Social/IconInstagram.vue";
import IconLinkedin from "../Icons/Social/IconLinkedin.vue";
import IconXing from "../Icons/Social/IconXing.vue";
import IconYoutube from "../Icons/Social/IconYoutube.vue";
import { Icon } from "@iconify/vue";

import NavigationUIFlyout from "./UI/NavigationUIFlyout.vue";
import NavigationUISlidedown from "./UI/NavigationUISlidedown.vue";
import NavigationUILanguageFlyout from "./UI/NavigationUILanguageFlyout.vue";
import NavigationUILanguageSlidedown from "./UI/NavigationUILanguageSlidedown.vue";
import NavigationCta from "./UI/NavigationCta.vue";

import { globalMethods } from "../GlobalUI/mixins/global.js";
import { moduleMixins } from "../../utils/mixins/moduleMixins.js";

export default {
  name: "Navigation",
  components: {
    IconCompanyLogo,
    IconFacebook,
    IconX,
    IconInstagram,
    IconLinkedin,
    IconXing,
    IconYoutube,
    NavigationUILanguageFlyout,
    NavigationUILanguageSlidedown,
    NavigationUIFlyout,
    NavigationUISlidedown,
    NavigationCta,
    Icon,
  },
  mixins: [globalMethods, moduleMixins],
  props: {
    navigationGlobal: {
      type: Object,
      default() {
        return navigationGlobal;
      },
    },
    socialMediaGlobal: {
      type: Object,
      default() {
        return socialMediaGlobal;
      },
    },
    brandingGlobal: {
      type: Object,
      default() {
        return brandingGlobal;
      },
    },
    pageLocale: {
      type: String,
      default: "de-DE",
    },
    jobShopUrl: {
      type: String,
      default: "",
    },
  },
  emits: ["showMenu", "emittedClick"],
  data() {
    return {
      showMenu: false,
      lastScrollTop: 0,
      navbarTop: 0,
      activeMobileGroup: -1,
    };
  },
  computed: {
    effectiveNavigationBarSize() {
      return (
        this.navigationGlobal?.config?.general?.navigationBarSize?.value ||
        "small"
      );
    },
    navWrapperElStyles() {
      return {
        height: this.showMenu ? "100dvh" : null,
      };
    },
    headerElStyles() {
      const { textColor, backgroundColor, hoverColor } =
        this.navigationGlobal.config.general;

      return {
        color: textColor.value,
        backgroundColor: backgroundColor.value,
        top: this.navbarTop,
        "--cr-navigation-hover-color": hoverColor.value,
      };
    },
    flyoutElStyles() {
      const { textColor, backgroundColor } =
        this.navigationGlobal.config.general;

      return {
        color: textColor.value,
        backgroundColor: backgroundColor.value,
        "--cr-navigation-flyout-bg-color": backgroundColor.value,
      };
    },
    menuButtonIconElStyles() {
      const { textColor } = this.navigationGlobal.config.general;

      return {
        stroke: textColor.value,
      };
    },
  },
  watch: {
    effectiveNavigationBarSize() {
      this.applyRootNavClass();
    },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll);
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    this.applyRootNavClass();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.handleResize);
    // Ensure global class is removed when component unmounts
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("cr-nav--large");
    }
  },
  methods: {
    applyRootNavClass() {
      if (typeof document === "undefined") return;
      if (this.effectiveNavigationBarSize === "large") {
        document.documentElement.classList.add("cr-nav--large");
      } else {
        document.documentElement.classList.remove("cr-nav--large");
      }
    },
    handleResize() {
      // Do not show mobile menu if window gets resized bigger without clicking on the menu button before
      if (this.$refs?.header?.clientWidth > 1033) {
        this.showMenu = false;
      }
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
      if (!this.showMenu) {
        this.activeMobileGroup = -1;
      }
      this.$emit("showMenu", this.showMenu);
    },
    handleLanguageSwitch(event) {
      const redirectUrl = event.target.value;

      window.location.assign(redirectUrl);
    },
    getComponentName(socialChannel) {
      return `Icon${socialChannel.label[0].toUpperCase()}${socialChannel.label.slice(
        1,
      )}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@include reset;

.nav-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
}

header {
  @include font-family("body");

  container-type: inline-size;
  contain: layout;

  box-sizing: border-box;
  width: 100%;

  font-style: normal;

  @include scale-fonts("navigation");

  transition: top 150ms ease-out;

  .outer-wrapper {
    box-sizing: border-box;
    height: inherit;
    padding: 0 30px;

    border-bottom: 0.25px solid rgb(22 22 22 / 10%);

    transition: all 300ms ease-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    @include min-laptop {
      height: var(--cr-nav-height, 49px);

      &.-large {
        --cr-nav-height: 78px;
        @include scale-fonts("navigation-large");

        .logo {
          height: 40px;
        }

        .links-wrapper {
          height: 78px;
        }

        .buttons {
          height: 78px;
        }

        :deep(.navigation-flyout-trigger) {
          @include scale-fonts("navigation-large");
        }
      }
    }

    &.-menu-open {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;

      height: 100dvh;
      padding: 16px 25px;
      overflow: hidden scroll;
    }
  }

  .inner-wrapper {
    width: 100%;

    &.-menu-open {
      display: flex;
      flex-direction: column;
      gap: 0;
      align-items: flex-start;
      align-self: stretch;
    }
  }

  .top-container {
    display: flex;
    flex-direction: column;
    gap: 25px;
    align-items: flex-start;
    align-self: stretch;
  }

  .top-row {
    display: flex;
    align-items: center;
    align-self: stretch;
    justify-content: space-between;
  }
}

.links-wrapper {
  display: flex;
  gap: 25px;
  align-items: center;
  height: 48px;
}

.logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  max-width: 200px;
  height: 30px;

  object-fit: contain;
}

.buttons {
  display: flex;
  gap: 25px;
  align-items: center;

  min-width: fit-content;
  height: 48px;
}

ul {
  display: flex;
  align-items: center;
  list-style: none;

  a {
    color: inherit;
    text-decoration: none;
  }
}

.desktop-nav {
  text-align: center;

  ul {
    gap: 12px;
  }

  li a {
    padding: 8px 12px 8px 0;
  }

  @include tablet {
    display: none;
  }
}

.language-select-desktop {
  @include tablet {
    display: none;
  }
}

.language-select-mobile {
  display: none;
  border-bottom: 1px solid;
  margin-block: 30px;
  width: 100%;

  nav {
    width: 100%;
  }

  @include tablet {
    display: flex;
    flex: 1;
  }
}

// CTA styles moved to NavigationCta.vue component

// burger menu on mobile
.menu-button {
  cursor: pointer;

  overflow: visible;
  display: none;

  width: 30px;
  height: 30px;
  margin: 0;
  padding: 0;

  font: inherit;
  line-height: normal;
  color: inherit;
  text-align: inherit;

  appearance: none;
  background: transparent;
  border: none;
  border-radius: 100%;

  transition: background-color 300ms ease-out;

  @include tablet {
    display: flex;
    place-items: center;
    justify-content: center;

    &.-menu-open {
      background-color: #ededef;
    }
  }

  svg {
    width: 20px;

    g {
      stroke-linecap: round;
      stroke-width: 8px;
    }

    .top {
      transform-origin: 50px 50px;
      transition: all 300ms ease-out;

      &.-menu-open {
        transform: rotate(-45deg) translateY(25px);
        stroke: #a0a0a2;
      }
    }

    .bottom {
      transform-origin: 50px 50px;
      transition: all 300ms ease-out;

      &.-menu-open {
        transform: rotate(45deg) translateY(-25px);
        stroke: #a0a0a2;
      }
    }
  }
}

.mobile-nav {
  @include font-family("navigation");
  @include scale-fonts("navigation");

  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-start;
  width: 100%;

  ul {
    @include reset-list;
    flex-direction: column;
    align-items: flex-start;
    margin: 0;
    padding: 0;

    li {
      padding-block: 10px;
    }

    a {
      display: flex;
    }
  }

  ul,
  li,
  a {
    width: 100%;
  }

  .-has-dropdown {
    border-bottom: 1px solid;
  }
}

.bottom-links {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  place-content: space-between;
  width: 100%;

  .social-links {
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 32px;
    margin-block: 32px;
    width: 100%;

    a {
      &:visited,
      &:link,
      &:focus,
      &:hover {
        cursor: pointer;
        color: currentcolor;
      }
    }
  }
}

/* Vue transition classes */
.fade-enter-active {
  transition: opacity 300ms ease-out;
  transition-delay: 150ms;
}

.fade-leave-active {
  display: none;
  transition: opacity 300ms ease-in;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition:
    opacity 300ms,
    transform 300ms;
}

.slide-fade-leave-active {
  transition:
    opacity 300ms,
    transform 300ms;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  transform: translateX(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
